import { Analytics } from './analytics.js';
import { SNOWPLOW_EVENTS_SCHEMA } from './constants.js';

const analytics = new Analytics('1.1.0');

export const ANALYTICS_CACHE_KEY = "mb_events";
export const USER_DETAILS_KEY = "mb_user_details";
export const ANALYTICS_VERSION = "2-0-0";

/**
 * interface Event {
 *  schema: string;
 *  payload: Record<string, string>
 * }
 */

/**
 * Queue events in localStorage until the events are ready to be dispatched
 * @param {Object} event - The event schema and payload of the event 
 */
export const queueEvent = (event) => {
  if (localStorage) {
    let events = localStorage.getItem(ANALYTICS_CACHE_KEY);
    events = events === null ? [] : JSON.parse(events);
    if (!events.some(ev => ev.schema === event.schema)) {
      events.push(event)
    }
    localStorage.setItem(ANALYTICS_CACHE_KEY, JSON.stringify(events))
  }
}

// helper method to get user details
const _getUserDetails = () => {
  let userDetails = localStorage.getItem(USER_DETAILS_KEY);
  userDetails = userDetails === null ? null : JSON.parse(userDetails);
  return userDetails;
}

/**
 * Send event to snowplow
 */
export const sendEvent = (event) => {
  const userDetails = _getUserDetails();
  analytics.sendToSnowplow(event.schema, { ...event.payload, ...userDetails });
}

/**
 * Send event to snowplow immediately without waiting for user details
 * Used for events that occur before login (e.g., login failures)
 */
export const sendEventImmediately = (event) => {
  analytics.sendToSnowplow(event.schema, event.payload);
}

/**
 * Dispatch events if available
 */
export const dispatchEvents = () => {
  if (localStorage) {
    const events = JSON.parse(localStorage.getItem(ANALYTICS_CACHE_KEY))
    if (events) events.forEach(ev => sendEvent(ev))
    // clearing events from the cache
    localStorage.removeItem(ANALYTICS_CACHE_KEY)
  }
}

// helper method to handle events based on the availability of user details
const processEvent = (schema, payload) => {
  const userDetails = _getUserDetails();
   // if queued events exist then dispatch them if user details exists.
  const queuedEvents = localStorage.getItem(ANALYTICS_CACHE_KEY);
  if (userDetails && queuedEvents) dispatchEvents();
  if (userDetails) {
    // Append userDetails to the payload and send the event immediately.
    sendEvent({ schema, payload: { ...payload, timestamp: new Date().toISOString() } });
  } else {
    // Queue the event until userDetails are available.
    queueEvent({ schema, payload: { ...payload, timestamp: new Date().toISOString() } });
  }
};

export const trackEventV1 = {

  /**
   * Event schema
   * @param {string} platform
   * @param {string} mbuuid
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [appversion] (Optional)
   * @param {string} [email] (Optional)
   */
  homepageLanded: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_HOMEPAGE_LANDED, payload),
  
  /**
   * Event schema
   * @param {string} platform
   * @param {string} mbuuid
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [appversion] (Optional)
   * @param {string} [email] (Optional)
   */
  createClaim: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_CREATE_CLAIM, payload),
  
  /**
   * Event schema
   * @param {string} [platform] (Optional)
   * @param {string} mbuuid
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [appversion] (Optional)
   * @param {string} [email] (Optional)
   * @param {string} [patient_name] (Optional)
   */
  userDetailsNext: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_USER_DETAILS_NEXT, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} mbuuid
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [appversion] (Optional)
   * @param {string} [pincode] (Optional)
   * @param {string} service_name
   */
  saveBill: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_SAVE_BILL, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} mbuuid
   * @param {string} [mbuserid] (Optional)
   * @param {string} pmentityid
   * @param {string} employee_email
   */
  submitClaim: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_SUBMIT_CLAIM, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} appversion
   * @param {string} pmentityid
   */
  uploadBillClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_BILL_CLICK, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} appversion
   * @param {string} pmentityid
   */
  uploadRxClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_RX_CLICK, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} appversion
   * @param {string} pmentityid
   */
  uploadReportClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_REPORT_CLICK, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} appversion
   * @param {string} pmentityid
   * @param {string} message
   */
  uploadBillSuccess: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_BILL_SUCCESS, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} appversion
   * @param {string} pmentityid
   * @param {string} message
   */
  uploadRxSuccess: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_RX_SUCCESS, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} appversion
   * @param {string} pmentityid
   * @param {string} message
   */
  uploadReportSuccess: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_REPORT_SUCCESS, payload),
  
  /**
   * Event schema
   * @param {string} mbsessionid
   * @param {string} platform
   * @param {string} orderid
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} message
   * @param {string} errormsg
   * @param {string} errorResponse
   */
  uploadRxFail: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_RX_FAIL, payload),
  

   /**
   * Event schema
   * @param {string} mbsessionid
   * @param {string} platform
   * @param {string} orderid
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} message
   * @param {string} errormsg
   * @param {string} errorResponse
   */
  uploadBillFail: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_BILL_FAIL, payload),

  /**
   * Event schema
   * @param {string} mbsessionid
   * @param {string} platform
   * @param {string} orderid
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} message
   * @param {string} errormsg
   * @param {string} errorResponse
   */
  uploadReportFail: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_REPORT_FAIL, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} pmentityid
   */
  yourClaimsClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_YOUR_CLAIMS_CLICK, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} pmentityid
   * @param {string} name
   */
  homeBannerClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_HOME_BANNER, payload),
  
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} pmentityid
   * @param {string} name
   */
  claimBannerClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_CLAIM_BANNER, payload),


resubmitBillSaveClick: (payload = {}) =>
  processEvent(
    SNOWPLOW_EVENTS_SCHEMA.REIMBURSEMENT_RESUBMIT_BILL_SAVE_CLICKS,
    payload
   
  ),

   /**
   * Event schema
   * @param {string} platform 
   * @param {string} mbsessionid
   * @param {string} mbuserid
   * @param {string} [appversion] (Optional)
   * @param {string} [osversion] (Optional)
   * @param {string} [screenName] (Optional)
   * @param {string} mbuuid 
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [patientid] (Optional)
   */

resubmitClaimSaveClick: (payload = {}) =>
  processEvent(
    SNOWPLOW_EVENTS_SCHEMA.REIMBURSEMENT_RESUBMIT_CLAIM_SAVE_CLICKS,
    payload
  ),
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} mbsessionid
   * @param {string} mbuserid
   * @param {string} [appversion] (Optional)
   * @param {string} [osversion] (Optional)
   * @param {string} [screenName] (Optional)
   * @param {string} mbuuid 
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [patientid] (Optional)
   */
   
}

const trackEventV2 = {
 /**
  * Event schema
  * @param {string} platform
  * @param {string} mbuuid
  * @param {string} mbuserid
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  * @param {string} [appversion] (Optional)
  * @param {string} [email] (Optional)
  */
 homepageLanded: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_HOMEPAGE_LANDED, payload),
 
 /**
  * Event schema
  * @param {string} platform
  * @param {string} mbuuid
  * @param {string} mbuserid
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  * @param {string} [appversion] (Optional)
  * @param {string} [email] (Optional)
  */
 createClaim: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_CREATE_CLAIM, payload),
 
 /**
  * Event schema
  * @param {string} [platform] (Optional)
  * @param {string} mbuuid
  * @param {string} mbuserid
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  * @param {string} [appversion] (Optional)
  * @param {string} [email] (Optional)
  * @param {string} [patient_name] (Optional)
  */
 userDetailsNext: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_USER_DETAILS_NEXT, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} mbuuid
  * @param {string} mbuserid
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  * @param {string} [appversion] (Optional)
  * @param {string} [pincode] (Optional)
  * @param {string} service_name
  */
 saveBill: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_SAVE_BILL, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} mbuuid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  * @param {string} [mbuserid] (Optional)
  * @param {string} pmentityid
  * @param {string} employee_email
  */
 submitClaim: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_SUBMIT_CLAIM, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} appversion
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadBillClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_BILL_CLICK, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} appversion
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadRxClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_RX_CLICK, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} appversion
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadReportClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_REPORT_CLICK, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} appversion
  * @param {string} pmentityid
  * @param {string} message
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadBillSuccess: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_BILL_SUCCESS, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} appversion
  * @param {string} pmentityid
  * @param {string} message
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadRxSuccess: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_RX_SUCCESS, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} appversion
  * @param {string} pmentityid
  * @param {string} message
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadReportSuccess: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_REPORT_SUCCESS, payload),
 
 /**
  * Event schema
  * @param {string} mbsessionid
  * @param {string} platform
  * @param {string} orderid
  * @param {string} mbuserid
  * @param {string} pmentityid
  * @param {string} message
  * @param {string} errormsg
  * @param {string} errorResponse
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadRxFail: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_RX_FAIL, payload),
 

  /**
  * Event schema
  * @param {string} mbsessionid
  * @param {string} platform
  * @param {string} orderid
  * @param {string} mbuserid
  * @param {string} pmentityid
  * @param {string} message
  * @param {string} errormsg
  * @param {string} errorResponse
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadBillFail: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_BILL_FAIL, payload),

 /**
  * Event schema
  * @param {string} mbsessionid
  * @param {string} platform
  * @param {string} orderid
  * @param {string} mbuserid
  * @param {string} pmentityid
  * @param {string} message
  * @param {string} errormsg
  * @param {string} errorResponse
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 uploadReportFail: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_UPLOAD_REPORT_FAIL, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} pmentityid
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 yourClaimsClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_YOUR_CLAIMS_CLICK, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} pmentityid
  * @param {string} name
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 homeBannerClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_HOME_BANNER, payload),
 
 /**
  * Event schema
  * @param {string} platform 
  * @param {string} pmentityid
  * @param {string} name
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} [patientid] (Optional)
  */
 claimBannerClick: (payload = {}) => processEvent(SNOWPLOW_EVENTS_SCHEMA.MB_REIM_CLAIM_BANNER, payload), 

 resubmitBillSaveClick: (payload = {}) =>
  processEvent(
    SNOWPLOW_EVENTS_SCHEMA.REIMBURSEMENT_RESUBMIT_BILL_SAVE_CLICKS,
    payload
  ),
   /**
   * Event schema
   * @param {string} platform 
   * @param {string} mbsessionid
   * @param {string} mbuserid
   * @param {string} [appversion] (Optional)
   * @param {string} [osversion] (Optional)
   * @param {string} [screenName] (Optional)
   * @param {string} mbuuid 
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [patientid] (Optional)
   */

resubmitClaimSaveClick: (payload = {}) =>
  processEvent(
    SNOWPLOW_EVENTS_SCHEMA.REIMBURSEMENT_RESUBMIT_CLAIM_SAVE_CLICKS,
    payload
  ),
  /**
   * Event schema
   * @param {string} platform 
   * @param {string} mbsessionid
   * @param {string} mbuserid
   * @param {string} [appversion] (Optional)
   * @param {string} [osversion] (Optional)
   * @param {string} [screenName] (Optional)
   * @param {string} mbuuid 
   * @param {string} mbuserid
   * @param {string} pmentityid
   * @param {string} [patientid] (Optional)
   */

 /**
  * Event schema - Track login/authentication failures
  * Note: This event is sent immediately without waiting for user details
  * since it occurs during login failure when user details aren't available
  * @param {string} platform 
  * @param {string} [appversion] (Optional)
  * @param {string} [osversion] (Optional)
  * @param {string} [screenName] (Optional)
  * @param {string} mbuuid 
  * @param {string} mbuserid
  * @param {string} [patientid] (Optional)
  * @param {string} pmentityid 
  * @param {string} ReimbusementUnableToLoginReason 
  */
 loginPopIssue: (payload = {}) =>
   sendEventImmediately({ 
     schema: SNOWPLOW_EVENTS_SCHEMA.REIMBURSEMENT_LOGIN_POP_ISSUE, 
     payload: { ...payload, timestamp: new Date().toISOString() } 
   }),
}

const handlerMap = {
  "1-0-0": trackEventV1,
  "2-0-0": trackEventV2
}

export const trackEvent = {
  ...handlerMap[ANALYTICS_VERSION]
}
